import * as T from 'prop-types';
import styled from 'styled-components/macro';

import { Badge, Card } from '@olist/design-system';
import { Flex, Text } from '@olist/united';

import { AnimatedImage } from '../layout';

const List = styled.ul`
  list-style: disc;
  padding-left: 5%;
`;

const BannerLabel = ({ title, ...rest }) => {
  return title && <Text {...rest}>{title}</Text>;
};

const BannerList = ({ items, label }) => {
  return (
    <>
      {label && <BannerLabel title={label} mb={1} />}
      {items?.length ? (
        <List>
          {items.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <BannerLabel title={item.text} />
            </li>
          ))}
        </List>
      ) : null}
    </>
  );
};

const BannerBottomLink = ({ label, link }) => {
  return (
    link &&
    label && (
      <Text.Link href={link} mt={6} mb={10}>
        *{label}
      </Text.Link>
    )
  );
};

const BannerBottomLabel = ({ label }) => {
  return (
    label && (
      <Text mt={6} mb={10}>
        *{label}
      </Text>
    )
  );
};

const BannerBottomInfo = ({ label, link }) => {
  if (link && label) {
    return BannerBottomLink({ link, label });
  }
  if (label) {
    return BannerBottomLabel({ label });
  }
  return null;
};

const BannerBadge = ({ badgeColor, showBadgeIcon, title }) => {
  return (
    title &&
    badgeColor && (
      <Flex>
        <Badge showIcon={showBadgeIcon} variation={badgeColor}>
          {title}
        </Badge>
      </Flex>
    )
  );
};

const BannerTitle = ({ title }) => {
  return (
    <BannerLabel
      color="foundation.primaryDefault"
      fontWeight="semibold"
      lineHeight="1.2"
      mt="12px"
      mb={3}
      fontSize={5}
      width={1}
      title={title}
    />
  );
};

const BannerBody = ({
  badgeColor,
  badgeLabel,
  link,
  linkLabel,
  listItems,
  listLabel,
  showBadgeIcon,
  subTitle,
  title,
}) => {
  return (
    <>
      <BannerBadge title={badgeLabel} badgeColor={badgeColor} showBadgeIcon={showBadgeIcon} />
      <BannerTitle title={title} />
      <BannerLabel mb="12px" title={subTitle} />
      <BannerList items={listItems} label={listLabel} />
      <BannerBottomInfo label={linkLabel} link={link} />
    </>
  );
};

const Banner = ({ borderColor, children, file, loop }) => {
  return (
    <Card borderDirection="left" borderColor={borderColor} style={{ maxWidth: '448px', flex: '2' }}>
      <Card.Body>
        <Flex flexDirection="column">
          <AnimatedImage loop={loop} src={file} style={{ maxWidth: '448px' }} />
          {children}
        </Flex>
      </Card.Body>
    </Card>
  );
};

BannerBadge.propTypes = {
  title: T.node,
  badgeColor: T.string,
  showBadgeIcon: T.bool,
};
BannerBadge.defaultProps = {
  title: '',
  badgeColor: '',
  showBadgeIcon: false,
};

BannerLabel.propTypes = {
  title: T.node,
};
BannerLabel.defaultProps = {
  title: '',
};

BannerList.propTypes = {
  items: T.arrayOf(T.shape({ text: T.node.isRequired })),
  label: T.string,
};

BannerList.defaultProps = {
  items: [],
  label: '',
};

BannerBottomLink.propTypes = {
  link: T.string,
  label: T.node,
};

BannerBottomLink.defaultProps = {
  link: '',
  label: '',
};

BannerBottomLabel.propTypes = {
  label: T.node,
};

BannerBottomLabel.defaultProps = {
  label: '',
};
BannerBody.propTypes = {
  title: T.node,
  subTitle: T.node,
  listItems: T.arrayOf(T.shape({ text: T.node.isRequired })),
  listLabel: T.string,
  link: T.string,
  badgeLabel: T.string,
  badgeColor: T.string,
  showBadgeIcon: T.bool,
  linkLabel: T.string,
};

BannerBody.defaultProps = {
  title: '',
  subTitle: '',
  linkLabel: '',
  listItems: [],
  listLabel: '',
  link: '',
  badgeLabel: '',
  badgeColor: '',
  showBadgeIcon: false,
};

BannerTitle.propTypes = {
  title: T.string.isRequired,
};

Banner.defaultProps = {
  loop: false,
  borderColor: 'accent',
};

Banner.propTypes = {
  children: T.node.isRequired,
  file: T.shape({}).isRequired,
  loop: T.bool,
  borderColor: T.string,
};

BannerBottomInfo.propTypes = {
  link: T.string,
  label: T.string,
};

BannerBottomInfo.defaultProps = {
  link: ``,
  label: ``,
};

Banner.Body = BannerBody;

export default Banner;
